import { useEffect, useState } from 'react';
import type { CosmeticView, HallOfFameEntry, RankingRow, RankingsView } from '@galaxis/shared';
import { SEASON_SCORE_WEIGHTS } from '@galaxis/shared';
import { fetchRankings, fetchHallOfFame, fetchCosmetics, equipCosmetic } from '../../api/social.ts';

const STAT_LABELS: Array<[keyof typeof SEASON_SCORE_WEIGHTS, string]> = [
  ['systems', 'rendszer'], ['techs', 'technológia'], ['battles', 'győzelem'], ['stations', 'állomás'], ['events', 'döntés'],
];

function Table({ rows, kind }: { rows: RankingRow[]; kind: 'player' | 'alliance' }) {
  if (rows.length === 0) return <p className="text-sm text-slate-500">Még nincs adat.</p>;
  return (
    <ol className="space-y-1.5" data-testid={`ranking-${kind}`}>
      {rows.map(r => (
        <li key={`${r.rank}-${r.name}`} data-you={r.isYou ? 'true' : undefined}
          className={`sci-fi-panel rounded-lg px-3 py-2 flex items-center gap-3 border ${r.isYou ? 'border-accent/60' : 'border-transparent'}`}>
          <span className="font-mono text-slate-500 w-6 text-right">{r.rank}.</span>
          <span className="flex-1 min-w-0">
            <span className="text-sm text-slate-100 truncate block">
              {r.allianceTag && <span className="text-accent mr-1">[{r.allianceTag}]</span>}
              {r.name}{r.isYou && <span className="text-xs text-accent ml-1">(te)</span>}
            </span>
            {r.title && <span className="text-[11px] text-amber-300 block">{r.title}</span>}
            {kind === 'player' && r.stats && (
              <span className="text-[11px] text-slate-500 block">
                {STAT_LABELS.filter(([k]) => r.stats![k] > 0).map(([k, l]) => `${r.stats![k]} ${l}`).join(' · ') || 'még nincs teljesítmény'}
              </span>
            )}
            {kind === 'alliance' && <span className="text-[11px] text-slate-500 block">{r.members} tag</span>}
          </span>
          <span className="font-mono text-accent">{r.score}</span>
        </li>
      ))}
    </ol>
  );
}

export function RankingsPanel() {
  const [data, setData] = useState<RankingsView | null>(null);
  const [hof, setHof] = useState<HallOfFameEntry[]>([]);
  const [cos, setCos] = useState<CosmeticView[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRankings().then(setData).catch(e => setError(e instanceof Error ? e.message : 'Hiba'));
    fetchHallOfFame().then(r => setHof(r.entries)).catch(() => undefined);
    fetchCosmetics().then(r => setCos(r.items)).catch(() => undefined);
  }, []);

  if (error) return <p className="text-sm text-red-400">{error}</p>;
  if (!data) return <p className="text-sm text-slate-500">Rangsor betöltése…</p>;

  const titles = cos.filter(c => c.kind === 'title');
  const seasons = [...new Set(hof.map(h => h.seasonNumber))];
  return (
    <div className="space-y-6">
      <section className="sci-fi-panel rounded-xl p-4" data-testid="season-banner">
        {data.season ? (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-100">{data.season.name}</h2>
              <span className="text-xs text-slate-400">
                {data.season.endsAt ? `Vége: ${new Date(data.season.endsAt).toLocaleString('hu-HU')}` : 'Nincs kitűzött végdátum'}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              A pontszám csak a szezon alatt elért eredményekből jön: {STAT_LABELS.map(([k, l]) => `${l} ×${SEASON_SCORE_WEIGHTS[k]}`).join(', ')}.
              {data.you && <> Jelenleg a(z) <b className="text-slate-200">{data.you.rank}.</b> vagy, {data.you.score} ponttal.</>}
            </p>
          </>
        ) : <p className="text-sm text-slate-400">Jelenleg nem fut szezon. A korábbi szezonok eredményei a dicsőségtáblán maradnak.</p>}
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <section aria-label="Játékosok"><h2 className="text-sm font-semibold text-slate-300 mb-2">Játékosok</h2><Table rows={data.players} kind="player" /></section>
        <section aria-label="Szövetségek"><h2 className="text-sm font-semibold text-slate-300 mb-2">Szövetségek</h2><Table rows={data.alliances} kind="alliance" /></section>
      </div>

      <section aria-label="Dicsőségtábla" data-testid="hall-of-fame">
        <h2 className="text-sm font-semibold text-slate-300 mb-2">Dicsőségtábla</h2>
        {seasons.length === 0 ? <p className="text-sm text-slate-500">Még egy szezon sem zárult le.</p> : seasons.map(n => (
          <div key={n} className="sci-fi-panel rounded-lg p-3 mb-2">
            <h3 className="text-sm text-slate-200 font-medium">{hof.find(h => h.seasonNumber === n)!.seasonName}</h3>
            <ul className="text-xs text-slate-400 mt-1 space-y-0.5">
              {hof.filter(h => h.seasonNumber === n && h.rank <= 3).map(h => (
                <li key={`${h.kind}${h.rank}${h.name}`}>{h.kind === 'player' ? '👤' : '🛡️'} {h.rank}. {h.name} <span className="font-mono text-accent">{h.score}</span></li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      {cos.length > 0 && (
        <section aria-label="Megőrzött címek" data-testid="cosmetics">
          <h2 className="text-sm font-semibold text-slate-300 mb-2">Megőrzött címeid és jelvényeid</h2>
          <ul className="flex flex-wrap gap-2">
            {cos.map(c => (
              <li key={c.key} className={`rounded-full border px-3 py-1 text-xs ${c.equipped ? 'border-amber-400 text-amber-300' : 'border-slate-600 text-slate-300'}`}>
                {c.kind === 'title' ? '🏅' : '🎖️'} {c.label}
              </li>
            ))}
          </ul>
          {titles.length > 0 && (
            <label className="block mt-3 text-xs text-slate-400">
              Viselt cím a rangsorban
              <select className="ml-2 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-200"
                value={titles.find(t => t.equipped)?.key ?? ''}
                onChange={e => void equipCosmetic(e.target.value || null).then(r => setCos(r.items))}>
                <option value="">(nincs)</option>
                {titles.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
          )}
        </section>
      )}
    </div>
  );
}
